import { PlayerManager } from './playerManager';
import { ICaptionsItem } from '../../../types/common';
import { IPlayerManagerListener } from './playerManagerListener';
import { PlayCaptionHandler } from './handlers/playCaptionHandler';
import { PlayCommonHandler } from './handlers/playCommonHandler';
import { PlayCaptionCommandHandler } from './handlers/playCaptionCommandHandler';
import { PlayerProvider } from '../provider/playerProvider';

export enum EPlayerControllerMode {
  PLAY_COMMON,
  PLAY_CAPTION,
  // one caption from an exercise/word timestamp or the lesson transcript, then stop regardless of autoPause
  PLAY_ONCE,
}

// caption bounds clip the first word's onset and the last word's tail a little
const PLAY_ONCE_LEAD_SEC = .3;
const PLAY_ONCE_TAIL_SEC = .3;

export class PlayerController implements IPlayerManagerListener {

  private static instance: PlayerController;

  public static init() {
    PlayerController.getInstance();
  }

  public static getInstance(): PlayerController {
    if (!PlayerController.instance)
      PlayerController.instance = new PlayerController();
    return PlayerController.instance;
  }

  private mode: EPlayerControllerMode = EPlayerControllerMode.PLAY_COMMON;
  private waitPauseTimer: any;
  private mousePause!: boolean;
  private playCaptionClickLastTime!: number;
  private playCaptionClickCancel!: boolean;
  private playOnceSeq = 0;
  private playOnceCaption: ICaptionsItem | null = null;

  private playCommonHandler: PlayCommonHandler;
  private playCaptionHandler: PlayCaptionHandler;
  private playCaptionCommandHandler: PlayCaptionCommandHandler;

  constructor() {
    PlayerManager.getInstance().addListener(this);
    this.playCommonHandler = new PlayCommonHandler();
    this.playCaptionHandler = new PlayCaptionHandler();
    this.playCaptionCommandHandler = new PlayCaptionCommandHandler();
  }

  public setMode(mode: EPlayerControllerMode) {
    this.mode = mode;
  }

  public getMode(): EPlayerControllerMode {
    return this.mode;
  }

  public play(time?: number, targetIndex?: number) {
    if (targetIndex !== undefined) {
      PlayerManager.getInstance().setTargetIndex(targetIndex);
    }
    this.playCommonHandler.play(time);
  }

  public onNativePlayClicked() {
    const playerController = PlayerController.getInstance();
    playerController.reset();
    playerController.setMode(EPlayerControllerMode.PLAY_COMMON);
    PlayerManager.getInstance().onNativePlayClicked();
  }

  public onNativePauseClicked() {
    this.reset();
    this.leavePlayOnce();
    PlayerManager.getInstance().onNativePauseClicked();
  }

  public pause() {
    this.reset();
    this.leavePlayOnce();
    PlayerManager.getInstance().pausePlay();
  }

  public togglePlayPause() {
    this.leavePlayOnce();
    if (!PlayerProvider.getProvider().isPaused()) {
      PlayerManager.getInstance().pausePlay();
    } else {
      this.playCaptionCommandHandler.resumePlay();
    }
  }

  public async playNext() {
    this.setMode(EPlayerControllerMode.PLAY_CAPTION);
    this.playCaptionCommandHandler.playNext();
  }

  public async playPrev() {
    this.setMode(EPlayerControllerMode.PLAY_CAPTION);
    this.playCaptionCommandHandler.playPrev();
  }

  public async repeat() {
    this.setMode(EPlayerControllerMode.PLAY_CAPTION);
    this.playCaptionCommandHandler.repeat();
  }

  public async playCaption(caption: ICaptionsItem, fromClick: boolean = false) {
    if (fromClick) {
      this.playCaptionClickLastTime = new Date().getTime();
      this.playCaptionClickCancel = false;
      const callback = this.playCaptionFromClickTimeOut.bind(this);
      setTimeout(() => { callback(caption) }, 500);
    } else {
      this.playCaptionHandler.play(caption);
    }
  }

  public isPlayingOnce(caption: ICaptionsItem): boolean {
    return this.mode === EPlayerControllerMode.PLAY_ONCE
      && this.playOnceCaption?.startTime === caption.startTime
      && !PlayerProvider.getProvider().isPaused();
  }

  // next — the following line: overlapping speakers can stretch endTime past it, so the stop is capped there
  public async playCaptionOnce(caption: ICaptionsItem, next?: ICaptionsItem) {
    const seq = ++this.playOnceSeq;
    this.playOnceCaption = caption;
    this.reset();
    this.setMode(EPlayerControllerMode.PLAY_ONCE);
    const playerManager = PlayerManager.getInstance();
    if (!playerManager.isCanPlayFromTime()) {
      // text lesson: TTS speaks just this caption and stops by itself
      await playerManager.startPlay(undefined, caption.text);
      return;
    }
    await playerManager.startPlay(Math.max(0, caption.startTime - PLAY_ONCE_LEAD_SEC));
    // startPlay clears the timer; a newer click may have started while this one was seeking
    if (seq === this.playOnceSeq && this.mode === EPlayerControllerMode.PLAY_ONCE) {
      const end = caption.endTime + PLAY_ONCE_TAIL_SEC;
      playerManager.setTimer(next && next.startTime > caption.startTime ? Math.min(end, next.startTime) : end);
    }
  }

  // any manual play/pause ends the one-caption mode, so autoPause works again afterwards
  private leavePlayOnce() {
    if (this.mode === EPlayerControllerMode.PLAY_ONCE) {
      this.mode = EPlayerControllerMode.PLAY_COMMON;
    }
  }

  private async playCaptionFromClickTimeOut(caption: ICaptionsItem) {
    if (!this.playCaptionClickCancel) {
      const time = await PlayerProvider.getProvider().getCurrentTime();
      if (time &&
        caption.startTime < time &&
        caption.endTime > time
      ) {
        PlayerManager.getInstance().startPlay();
      } else {
        this.playCaptionHandler.play(caption);
      }
    }
  }

  public async pausePlay(fromSelectText: boolean = false) {
    if (fromSelectText) {
      const period = new Date().getTime() - this.playCaptionClickLastTime;
      if (period < 500) {
        this.playCaptionClickCancel = true;
      }
    }
    PlayerManager.getInstance().pausePlay();
  }

  public mouseEnter(): boolean {
    const isPlay = PlayerProvider.getProvider().isPlay();
    if (isPlay || this.waitPauseTimer) {
      PlayerManager.getInstance().pausePlay();
      this.mousePause = true;
      if (this.waitPauseTimer) {
        clearTimeout(this.waitPauseTimer);
        this.waitPauseTimer = null;
      }
      PlayerManager.getInstance().focusNotify(isPlay);
      return true;
    }
    return false;
  }

  public mouseLeave() {
    if (this.mousePause) {
      this.waitPauseTimer = setTimeout(() => {
        this.mousePause = false;
        PlayerManager.getInstance().startPlay();
      }, 100);
    }
  }

  public onDeselectText() {
    if (this.mousePause) {
      this.mousePause = false;
      PlayerManager.getInstance().startPlay();
    }
  }

  public resetMode() {
    this.mode = EPlayerControllerMode.PLAY_COMMON;
    this.resetWaitPauseTimer();
    this.mousePause = false;
  }

  public reset() {
    this.resetWaitPauseTimer();
    this.mousePause = false;
  }

  onNativeIndexChange(index: number): void {
  }

  onTargetIndexChange(index: number): void {
    if (this.mode === EPlayerControllerMode.PLAY_CAPTION) {
      this.playCaptionHandler.onTargetIndexChange(index);
    } else if (this.mode === EPlayerControllerMode.PLAY_COMMON) {
      this.playCommonHandler.onTargetIndexChange(index);
    }
  }

  onTimerDone() {
    if (this.mode !== EPlayerControllerMode.PLAY_ONCE) return;
    this.mode = EPlayerControllerMode.PLAY_COMMON;
    // TTS stops by itself; for text this may fire early (a cancelled phrase's onend) — then only the mode resets
    if (PlayerManager.getInstance().isCanPlayFromTime()) {
      PlayerManager.getInstance().pausePlay();
    }
  }

  public startWaitTimer(time: number) {
    this.waitPauseTimer = setTimeout(this.resumeAfterPauseFinish.bind(this), time);
  }

  private resetWaitPauseTimer() {
    if (this.waitPauseTimer) {
      clearTimeout(this.waitPauseTimer);
      this.waitPauseTimer = null;
    }
  }

  public resumeAfterPauseFinish() {
    if (this.mode === EPlayerControllerMode.PLAY_COMMON || this.mode === EPlayerControllerMode.PLAY_CAPTION) {
      PlayerManager.getInstance().startPlay();
    }
  }

  public onStarPlay() {
    this.playCommonHandler.onStarPlay();
  }
}
